import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, take, tap } from 'rxjs/operators';

import { ConfigService, StorageService } from '@venecia/services';
import { OAuthApi } from '@venecia/apis/auth/oauth.api';
import { Bearer } from '@venecia/apis/auth/model/bearer.model';
import { GlobalConstants } from '@venecia/constants';
import { ThemeName } from '@venecia/models';

/**
 * OAuth 2.0 Authorization Code + PKCE client for SPA.
 *
 * Design notes:
 * - No HttpClient usage here (delegated to OAuthApi).
 * - Stores PKCE verifier in sessionStorage only.
 * - Stores bearer bundle using StorageService.
 */
@Injectable({ providedIn: 'root' })
export class OAuthService {
    private inFlightCodeExchange$: Observable<Bearer> | null = null;

    private readonly oauthApi = inject(OAuthApi);
    private readonly configService = inject(ConfigService);
    private readonly storage = inject(StorageService);
    private readonly router = inject(Router);

    private clientId?: string;
    private redirectPath?: string;
    private scope?: string;

    /**
     * Holds an in-flight refresh request so concurrent calls share it.
     */
    private inFlightRefresh$: Observable<Bearer> | null = null;

    protected constructor() {
        this.configService.select('oauth.clientId').pipe(take(1)).subscribe((v: string) => (this.clientId = v));
        this.configService.select('oauth.redirectPath').pipe(take(1)).subscribe((v: string) => (this.redirectPath = v));
        this.configService.select('oauth.scope').pipe(take(1)).subscribe((v: string) => (this.scope = v));
    }

    /**
     * Starts the Authorization Code flow (redirect to /authorize).
     * Stores PKCE verifier + state in sessionStorage.
     */
    startAuthorizationFlow(returnUrl?: string): void {
        const verifier = this.generateCodeVerifier();
        this.storage.set<string>(GlobalConstants.PKCE_VERIFIER_KEY, verifier);

        const state = crypto.randomUUID();
        this.storage.set<string>(GlobalConstants.OAUTH_STATE_KEY, state);

        if (returnUrl) {
            this.storage.set<string>(GlobalConstants.POST_LOGIN_URL_KEY, returnUrl);
        }

        this.generateCodeChallenge(verifier)
            .then((challenge) => {
                const authorizeUrl = this.oauthApi.buildAuthorizeUrl({
                    response_type: 'code',
                    client_id: this.clientId!,
                    redirect_uri: this.resolveRedirectUri(),
                    scope: this.scope ?? '',
                    state,
                    code_challenge: challenge,
                    code_challenge_method: 'S256',
                    lang: this.storage.get<string>(GlobalConstants.LANG_KEY) ?? "es",
                    theme: this.storage.get<ThemeName>(GlobalConstants.THEME_KEY) ?? "light"
                });
                window.location.href = authorizeUrl;
            })
            .catch(() => {
                window.location.reload();
            });
    }

    /**
     * Exchanges an authorization code for a bearer bundle.
     * Validates state (when provided).
     */
    exchangeCode$(code: string, state?: string | null): Observable<Bearer> {
        if (this.inFlightCodeExchange$) {
            return this.inFlightCodeExchange$;
        }

        const expectedState = this.storage.get<string>(GlobalConstants.OAUTH_STATE_KEY);

        if (expectedState && state && expectedState !== state) {
            return throwError(() => new Error('Invalid OAuth state'));
        }

        const verifier = this.storage.get<string>(GlobalConstants.PKCE_VERIFIER_KEY);
        if (!verifier) {
            return throwError(() => new Error('Missing PKCE verifier'));
        }

        this.inFlightCodeExchange$ = this.oauthApi.tokenAuthorizationCode({
            clientId: this.clientId!,
            code,
            redirectUri: this.resolveRedirectUri(),
            codeVerifier: verifier,
        }).pipe(
            take(1),
            tap((bearer) => this.storeBearer(bearer)),
            shareReplay({ bufferSize: 1, refCount: false }),
            finalize(() => {
                this.safeRemove(GlobalConstants.PKCE_VERIFIER_KEY);
                this.safeRemove(GlobalConstants.OAUTH_STATE_KEY);
                this.inFlightCodeExchange$ = null;
            })
        );

        return this.inFlightCodeExchange$;
    }

    /**
     * Returns the currently stored bearer, if any.
     */
    getBearer(): Bearer | null {
        return this.storage.get<Bearer>(GlobalConstants.OAUTH_BEARER_KEY);
    }

    /**
     * Refresh access token with refresh_token rotation.
     * Single-flight to avoid parallel refreshes.
     */
    refresh$(): Observable<Bearer> {
        if (this.inFlightRefresh$) {
            return this.inFlightRefresh$;
        }

        const current = this.getBearer();
        if (!current?.refresh_token) {
            return throwError(() => new Error('Missing refresh_token'));
        }

        this.inFlightRefresh$ = this.oauthApi.tokenRefresh({
            clientId: this.clientId!,
            refreshToken: current.refresh_token,
        }).pipe(
            take(1),
            map((resp: Bearer) => resp),
            tap((bearer) => this.storeBearer(bearer)),
            shareReplay({ bufferSize: 1, refCount: false }),
            finalize(() => {
                this.inFlightRefresh$ = null;
            }),
            catchError((err) => {
                this.inFlightRefresh$ = null;
                return throwError(() => err);
            })
        );

        return this.inFlightRefresh$;
    }

    /**
     * Optional helper for callback page to navigate after successful exchange.
     */
    navigatePostLogin(defaultUrl: string = '/'): void {
        const url = this.storage.get<string>(GlobalConstants.POST_LOGIN_URL_KEY) ?? defaultUrl;
        this.safeRemove(GlobalConstants.POST_LOGIN_URL_KEY);
        this.router.navigateByUrl(url).catch(() => this.router.navigateByUrl(defaultUrl));
    }

    private storeBearer(bearer: Bearer): void {
        this.storage.set<Bearer>(GlobalConstants.OAUTH_BEARER_KEY, bearer);
    }

    private resolveRedirectUri(): string {
        const path = this.redirectPath ?? '';
        // Ensure single slash join.
        const normalized = path.startsWith('/') ? path : `/${path}`;
        return `${window.location.origin}${normalized}`;
    }

    private generateCodeVerifier(): string {
        // RFC 7636: code_verifier length 43-128 chars; use 64 chars base64url.
        const array = new Uint8Array(64);
        crypto.getRandomValues(array);

        // Convert bytes -> base64url
        const base64 = btoa(String.fromCharCode(...array));
        return base64
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/g, '')
            .slice(0, 128);
    }


    private safeRemove(key: string): void {
        const anyStorage = this.storage as unknown as { remove?: (k: string) => void };
        if (typeof anyStorage.remove === 'function') {
            anyStorage.remove(key);
            return;
        }
        // Fallback: overwrite with an empty value.
        this.storage.set<string>(key, '');
    }

    private async generateCodeChallenge(verifier: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(verifier);
        const digest = await crypto.subtle.digest('SHA-256', data);
        const bytes = new Uint8Array(digest);

        // bytes -> base64url
        let binary = '';
        bytes.forEach((b) => (binary += String.fromCharCode(b)));
        const base64 = btoa(binary);

        return base64
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/g, '');
    }
}
